import { differenceInCalendarDays } from 'date-fns';
import SVG from 'react-inlinesvg';
import { formatDateCustom, formatUTCToZonedTimezoneAbr, formatNumber } from 'modules/I18N';
import arrowIcon from 'assets/icons/arrow_right.svg';
import calendarIcon from 'assets/icons/calendar.svg';
import eyeIcon from 'assets/icons/eye_empty.svg';
import frameIcon from 'assets/icons/frame.svg';
import { CampaignPlanDetails } from 'components/common/types/CampaignPlan.types';
import { ChipBookingStatus } from 'components/common/Deal/ChipBookingStatus/ChipBookingStatus';
import { DATE_RANGE_FORMAT, FORMAT_DATE_FALLBACK, LAST_UPDATED_DATE_TIME_FORMAT } from './consts';
import { getCampaignPlanDisplayName } from './utils/getCampaignPlanDisplayName';

const PublicCampaignPlanContent: React.FC<{
  campaignPlanDetails: CampaignPlanDetails;
  publicLocaleCode: string;
}> = ({ campaignPlanDetails, publicLocaleCode }) => {
  const {
    campaignId,
    campaignStatus,
    startDate,
    endDate,
    lastUpdatedAt,
    metaData: { isoCountryCode, timeZone, allocationReport },
    totalCampaignFrames,
    totalCampaignImpressions,
  } = campaignPlanDetails;

  const lastUpdatedDateFormatWithCampaignTimezone = { ...LAST_UPDATED_DATE_TIME_FORMAT, timeZone };
  const formattedStartDate = formatDateCustom(startDate, DATE_RANGE_FORMAT, isoCountryCode);
  const formattedEndDate = formatDateCustom(endDate, DATE_RANGE_FORMAT, isoCountryCode);
  const formattedLastUpdatedAt = formatDateCustom(
    lastUpdatedAt,
    lastUpdatedDateFormatWithCampaignTimezone,
    isoCountryCode,
    FORMAT_DATE_FALLBACK,
  ).replace(/,/, ' at');

  const timeZoneAbbreviation = formatUTCToZonedTimezoneAbr(lastUpdatedAt, timeZone, FORMAT_DATE_FALLBACK);
  const formattedLastUpdatedAtWithTimeZone = `${formattedLastUpdatedAt} ${formattedLastUpdatedAt === FORMAT_DATE_FALLBACK ? '' : timeZoneAbbreviation}`;

  const formattedCampaignImpressions = formatNumber({
    value: totalCampaignImpressions,
    localeCode: publicLocaleCode,
    digits: 0,
  });

  const formattedCampaignFrames = formatNumber({
    value: totalCampaignFrames,
    localeCode: publicLocaleCode,
    digits: 0,
  });

  const totalCampaignDays =
    startDate && endDate ? differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 1 : null;

  const campaignPlanDisplayName = getCampaignPlanDisplayName({
    advertiserName: campaignPlanDetails.advertiserName,
    brandName: campaignPlanDetails.brandName,
  });

  const renderInfoBox = (icon: string, label: string, value: string | number | JSX.Element): JSX.Element => (
    <div className="flex p-5 border rounded-xl flex-1 items-start gap-4">
      <div className="p-2 border rounded-lg">
        <SVG src={icon} className="h-5 w-5" />
      </div>
      <div>
        <p className="sub-header-base text-essential-secondary">{label}</p>
        <div className="header-base lg:header-xl flex items-center gap-2">{value}</div>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4">
        <div className="flex items-center p-2.5 gap-4">
          <h2 className="header-base md:header-2xl" data-test-id="campaign-plan-display-name">
            {campaignPlanDisplayName}
          </h2>
          <div className="block">
            <ChipBookingStatus status={campaignStatus} />
          </div>
        </div>
        <div className="flex gap-8">
          <div className="p-2.5">
            <p className="sub-header-base text-essential-secondary">Campaign ID</p>
            <p className="sub-header-base md:header-xl">{campaignId}</p>
          </div>
          {formattedLastUpdatedAt && (
            <div className="p-2.5">
              <p className="sub-header-base text-essential-secondary">Last updated</p>
              <p className="sub-header-base md:header-xl">{formattedLastUpdatedAtWithTimeZone}</p>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 w-full my-9 overflow-x-auto scrollbar-hidden">
        {renderInfoBox(
          calendarIcon,
          'Campaign date range',
          <div data-test-id="date-range">
            <div className="flex flex-wrap items-center gap-2">
              {!!startDate && formattedStartDate}
              <SVG src={arrowIcon} />
              {!!endDate && formattedEndDate}
            </div>
            {totalCampaignDays !== null && (
              <span className="header-base font-normal" data-test-id="total-campaign-days">
                ({totalCampaignDays} {totalCampaignDays === 1 ? 'day' : 'days'})
              </span>
            )}
          </div>,
        )}
        {allocationReport?.impressions?.planned &&
          renderInfoBox(eyeIcon, 'Campaign impressions', formattedCampaignImpressions)}
        {renderInfoBox(frameIcon, 'Campaign frames', formattedCampaignFrames)}
      </div>
    </>
  );
};

export default PublicCampaignPlanContent;
